import { Box, Chip, Paper, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'

const ResultChip = ({ label, passed }) => {
  return (
    <Chip
      icon={passed ? <CheckCircleIcon /> : <CancelIcon />}
      label={label}
      size="medium"
      sx={{
        backgroundColor: passed ? '#00B85F' : '#dc3545',
        color: '#FFFFFF',
        fontWeight: 500,
        '& .MuiChip-icon': {
          color: '#FFFFFF',
        },
      }}
    />
  )
}

export const VerificationResults = ({ vendorData, metadata }) => {
  const { t } = useTranslation(['common', 'messages'])

  if (!vendorData && !metadata) {
    return (
      <Paper elevation={0} className={styles.container}>
        <Typography variant="h5">{t('common:verificationResults')}</Typography>
        <Typography variant="body2" color="textSecondary">
          {t('common:noData')}
        </Typography>
      </Paper>
    )
  }

  // Get features from vendorData
  const features = vendorData?.features || []

  // Check status for each feature
  const idVerification = vendorData?.id_verification
  const faceMatch = vendorData?.face_match
  const liveness = vendorData?.liveness
  const ipAnalysis = vendorData?.ip_analysis

  // Get metadata results
  const renaperValidation = metadata?.renaperValidation
  const rulesExecution = metadata?.rulesExecution

  return (
    <Accordion defaultExpanded elevation={0} className={styles.accordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
        <Typography variant="h5" fontWeight={700}>
          {t('common:verificationResults')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={styles.accordionDetails}>
        {vendorData?.status && (
          <Box className={styles.section}>
            <Typography variant="subtitle2">{t('common:status')}:</Typography>
            <Typography variant="body1" fontWeight={600}>
              {vendorData.status === 'Approved' ? t('common:approved') : vendorData.status === 'Declined' ? t('common:declined') : vendorData.status}
            </Typography>
          </Box>
        )}

        <Box className={styles.resultsGrid}>
        {idVerification && (
          <Box className={styles.resultItem}>
            <Typography variant="subtitle2" mb={1}>
              {t('common:idVerification')}:
            </Typography>
            <ResultChip
              label={idVerification.status === 'Approved' ? t('common:approved') : idVerification.status === 'Declined' ? t('common:declined') : idVerification.status || t('common:unknown')}
              passed={idVerification.status === 'Approved'}
            />
          </Box>
        )}

        {faceMatch && (
          <Box className={styles.resultItem}>
            <Typography variant="subtitle2" mb={1}>
              {t('common:faceMatch')} ({t('common:score')}: {faceMatch.score}):
            </Typography>
            <ResultChip
              label={faceMatch.status === 'Approved' ? t('common:approved') : faceMatch.status === 'Declined' ? t('common:declined') : faceMatch.status || t('common:unknown')}
              passed={faceMatch.status === 'Approved'}
            />
          </Box>
        )}

        {liveness && (
          <Box className={styles.resultItem}>
            <Typography variant="subtitle2" mb={1}>
              {t('common:liveness')} ({t('common:score')}: {liveness.score}):
            </Typography>
            <ResultChip
              label={liveness.status === 'Approved' ? t('common:approved') : liveness.status === 'Declined' ? t('common:declined') : liveness.status || t('common:unknown')}
              passed={liveness.status === 'Approved'}
            />
          </Box>
        )}

        {ipAnalysis && (
          <Box className={styles.resultItem}>
            <Typography variant="subtitle2" mb={1}>
              {t('common:ipAnalysis')}:
            </Typography>
            <ResultChip
              label={ipAnalysis.status === 'Approved' ? t('common:approved') : ipAnalysis.status === 'Declined' ? t('common:declined') : ipAnalysis.status || t('common:unknown')}
              passed={ipAnalysis.status === 'Approved'}
            />
          </Box>
        )}

        {renaperValidation && idVerification?.issuing_state === 'ARG' && (
          <Box className={styles.resultItem}>
            <Typography variant="subtitle2" mb={1}>
              {t('common:renaperValidation')}:
            </Typography>
            <ResultChip
              label={renaperValidation.status === 'APPROVED' ? t('common:approved') : renaperValidation.status === 'REJECTED' ? t('common:declined') : renaperValidation.status || t('common:unknown')}
              passed={renaperValidation.status === 'APPROVED'}
            />
          </Box>
        )}

        {rulesExecution && (
          <Box className={styles.resultItem}>
            <Typography variant="subtitle2" mb={1}>
              {t('common:rulesExecution')}:
            </Typography>
            <ResultChip
              label={rulesExecution.status === 'APPROVED' ? t('common:approved') : rulesExecution.status === 'REJECTED' ? t('common:declined') : rulesExecution.status || t('common:unknown')}
              passed={rulesExecution.status === 'APPROVED'}
            />
          </Box>
        )}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
